import { observer } from "mobx-react-lite";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import React, { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import FDate from "../../../app/common/FDate/FDate";
import SectionHeading from "../../../app/common/SectionHeading/SectionHeading";
import StageTag from "../../../app/common/StageTag/StageTag";
import Loading from "../../../app/layout/Loading/Loading";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { appColors } from "../../../colors";
import agent from "../../../app/api/agent";
import "./ProjectListDataTable.css";

const Projects = () => {
  const rootStore = useContext(RootStoreContext);
  const { user } = rootStore.userStore;
  const { loadingProjects, fetchProjects, projectRegistry, projects, createUnlinkedProject, creatingUnlinkedProject } =
    rootStore.projectStore;
  const navigate = useNavigate();

  /* Inline editing */
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  /* Create Project Dialog */
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    projectName: "", targetName: "", modality: null, primaryOrg: null,
    managerEmail: null, participantEmails: [],
  });
  const [userOptions, setUserOptions] = useState([]);
  const [createErrors, setCreateErrors] = useState({});

  const openCreateDialog = async () => {
    setCreateForm({ projectName: "", targetName: "", modality: null, primaryOrg: null, managerEmail: null, participantEmails: [] });
    setCreateErrors({});
    try {
      const users = await agent.Admin.userList();
      setUserOptions(users.map(u => ({ label: `${u.displayName || u.email} (${u.email})`, value: u.email })));
    } catch { /* ignore */ }
    setShowCreateDialog(true);
  };

  const handleEditStart = (rowData) => {
    setEditingRowId(rowData.id);
    setEditValues({
      projectName: rowData.projectName || "",
      targetName: rowData.targetName || "",
      modality: rowData.modality || "",
    });
  };

  const handleEditCancel = () => {
    setEditingRowId(null);
    setEditValues({});
  };

  const handleEditSave = async (rowData) => {
    try {
      await agent.Projects.edit(rowData.id, {
        id: rowData.id,
        projectName: editValues.projectName,
        targetName: editValues.targetName,
        modality: editValues.modality,
      });
      // Update local state
      const updated = { ...rowData, projectName: editValues.projectName, targetName: editValues.targetName, modality: editValues.modality };
      projectRegistry.set(rowData.id, updated);
      setEditingRowId(null);
      setEditValues({});
    } catch (err) {
      console.error("Failed to save project:", err);
    }
  };

  const handleCreateProject = async () => {
    const errors = {};
    if (!createForm.projectName) errors.projectName = "Required";
    if (!createForm.primaryOrg) errors.primaryOrg = "Required";
    if (!createForm.modality) errors.modality = "Required";
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const payload = {
      projectName: createForm.projectName,
      targetName: createForm.targetName || "",
      modality: createForm.modality,
      primaryOrg: createForm.primaryOrg,
      supportingOrgs: [],
      strainId: "00000000-0000-0000-0000-000000000000",
      managerEmail: createForm.managerEmail || null,
      participantEmails: createForm.participantEmails || [],
    };
    const result = await createUnlinkedProject(payload);
    if (result !== null) {
      setShowCreateDialog(false);
      fetchProjects();
    }
  };

  /* Local State Management */

  useEffect(() => {
    if (projectRegistry.size === 0) {
      fetchProjects();
    }
  }, [fetchProjects, projectRegistry]); // eslint-disable-line react-hooks/exhaustive-deps

  /* local variables */

  const dt = useRef(null);

  /* STAGE FILTER */
  const stages = ["H2L", "LO", "SP", "IND", "P1"];

  const stageItemTemplate = (option) => {
    return <StageTag stage={option} />;
  };

  const stageFilter = (options) => (
    <MultiSelect
      value={options.value}
      options={stages}
      onChange={(e) => options.filterApplyCallback(e.value)}
      itemTemplate={stageItemTemplate}
      placeholder="Select a Stage"
      className="p-column-filter"
    />
  );
  /* END STAGE FILTER */

  /* STATUS FILTER */
  const statuses = ["Active", "Terminated"];

  const statusFilter = (options) => (
    <SelectButton
      value={options.value}
      options={statuses}
      onChange={(e) => options.filterApplyCallback(e.value)}
      className="p-column-filter p-button-sm"
    />
  );
  /* END STATUS FILTER */

  const TargetBodyTemplate = (rowData) => {
    const isEditing = editingRowId === rowData.id;
    if (isEditing) {
      return (
        <InputText value={editValues.targetName}
          onChange={(e) => setEditValues(p => ({ ...p, targetName: e.target.value }))}
          style={{ width: "100%", fontSize: "0.85rem" }} />
      );
    }
    return (
      <React.Fragment>
        <span className="p-column-title">Target</span>
        {rowData.targetName}
      </React.Fragment>
    );
  };

  const ProjectNameBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title">Project Name</span>
        <b>
          <NavLink to={"/pm/project/" + rowData.id + "/"}>{rowData.projectName}</NavLink>
        </b>
      </React.Fragment>
    );
  };

  const PrimaryOrganizationBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title">Primary Organization</span>
        {rowData.primaryOrg.alias}
      </React.Fragment>
    );
  };

  const StatusBodyTemplate = (rowData) => {
    if (rowData.status === "Active") {
      return (
        <React.Fragment>
          <span className="p-column-title">Status</span>
          <Tag className="table-status-active" value="Active" />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <span className="p-column-title">Status</span>
        <Tag className="table-status-inactive" value="Terminated" />
      </React.Fragment>
    );
  };

  const DateBodyTemplate = (rowData) => {
    let inputDate = new Date(rowData.haStart).setHours(0, 0, 0, 0);
    let stageDate = rowData.h2LStart;

    if (rowData.h2LEnabled) {
      inputDate = new Date(rowData.h2LStart).setHours(0, 0, 0, 0);
      stageDate = rowData.h2LStart;
    }
    if (rowData.loEnabled) {
      inputDate = new Date(rowData.loStart).setHours(0, 0, 0, 0);
      stageDate = rowData.loStart;
    }
    if (rowData.spEnabled) {
      inputDate = new Date(rowData.spStart).setHours(0, 0, 0, 0);
      stageDate = rowData.spStart;
    }
    if (rowData.indEnabled) {
      inputDate = new Date(rowData.indStart).setHours(0, 0, 0, 0);
      stageDate = rowData.indStart;
    }
    if (rowData.clinicalP1Enabled) {
      inputDate = new Date(rowData.clinicalP1Start).setHours(0, 0, 0, 0);
      stageDate = rowData.clinicalP1Start;
    }
    let todaysDate = new Date().setHours(0, 0, 0, 0);

    if (rowData.Status === "Active" && inputDate < todaysDate) {
      return (
        <React.Fragment>
          <span className="p-column-title">Date</span>
          <FDate
            className="p-column-title"
            timestamp={stageDate}
            color={"#FFECB3"}
          />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <span className="p-column-title">Date</span>
        <FDate
          className="p-column-title"
          timestamp={stageDate}
          color={"#000000"}
        />
      </React.Fragment>
    );
  };

  const StageBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title">Current Stage</span>
        <Tag
          className={`table-stage-${rowData.currentStage}`}
          value={rowData.currentStage}
        />
      </React.Fragment>
    );
  };

  /** Loading Overlay */
  if (loadingProjects) {
    return <Loading />;
  }

  if (!loadingProjects) {
    const allProjects = projects() || [];
    const total = allProjects.length;
    const active = allProjects.filter(p => p.status === "Active").length;
    const terminated = allProjects.filter(p => p.status === "Terminated").length;

    const breadCrumbItems = [
      { label: "Home", command: () => navigate("/d/") },
      { label: "Projects" },
    ];

    return (
      <div className="flex flex-column w-full fadein animation-duration-500">
        <div className="flex w-full pb-2">
          <BreadCrumb model={breadCrumbItems} />
        </div>
        <div className="flex w-full">
          <SectionHeading
            icon="icon icon-common icon-briefcase"
            heading="Project"
            color={appColors.sectionHeadingBg.project}
            headerExtra={
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontWeight: 700, color: "#fff", fontSize: "1.3rem" }}>{total}</span>
                  <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginLeft: 6 }}>📊 Total</span>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontWeight: 700, color: "#fff", fontSize: "1.3rem" }}>{active}</span>
                  <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginLeft: 6 }}>🟢 Active</span>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontWeight: 700, color: "#fff", fontSize: "1.3rem" }}>{terminated}</span>
                  <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginLeft: 6 }}>🛑 Terminated</span>
                </div>
              </div>
            }
            customButtons={[
              { label: "Create New", icon: "pi pi-plus", className: "p-button-sm subtable-toolbar-btn", action: () => openCreateDialog() },
            ]}
          />
        </div>
        <div className="flex w-full">
          <DataTable
            ref={dt}
            value={projects()}
            paginator
            rows={10}
            // header={header}
            className="w-full datatable-project-list"
            //globalFilter={globalFilter}
            emptyMessage="No projects found."
            filterDisplay="row"
          >

            <Column
              field="projectName"
              header="Project Name"
              body={ProjectNameBodyTemplate}
              filter
              filterMatchMode="contains"
              filterPlaceholder="Filter by Project"
              className="narrow-column"
            />

            <Column
              field="targetName"
              header="Target"
              body={TargetBodyTemplate}
              filter
              filterMatchMode="contains"
              filterPlaceholder="Filter by Target"
              className="narrow-column"
            />

            <Column
              field="modality"
              header="Modality"
              filter
              filterMatchMode="contains"
              className="narrow-column"
              body={(rowData) => {
                const isEditing = editingRowId === rowData.id;
                if (isEditing) {
                  return (
                    <Dropdown value={editValues.modality}
                      options={[
                        { label: "Small Molecule", value: "Small Molecule" },
                        { label: "RIPTAC", value: "RIPTAC" },
                        { label: "Antisense", value: "Antisense" },
                        { label: "Antibody", value: "Antibody" },
                      ]}
                      onChange={(e) => setEditValues(p => ({ ...p, modality: e.value }))}
                      style={{ width: "100%", fontSize: "0.85rem" }} />
                  );
                }
                return rowData.modality || "—";
              }}
            />

            <Column
              header="Project Manager"
              body={(rowData) => {
                const manager = (rowData.members || []).find(m => m.role === "manager");
                return manager ? manager.userEmail : "—";
              }}
              filter
              filterMatchMode="contains"
              className="narrow-column"
            />

            <Column
              field="primaryOrg.alias"
              header="Primary Organization"
              filter
              filterMatchMode="contains"
              body={PrimaryOrganizationBodyTemplate}
            />

            <Column
              field="status"
              header="Status"
              body={StatusBodyTemplate}
              filter
              filterElement={statusFilter}
              style={{ width: "250px" }}
              showFilterMenu={false}
            />

            <Column
              field="Date"
              header="Date"
              body={DateBodyTemplate}
              style={{ width: "100px" }}
              sortable
            />

            <Column
              field="currentStage"
              header="Current Stage"
              body={StageBodyTemplate}
              filter
              filterField="currentStage"
              filterElement={stageFilter}
              showFilterMenu={false}
              filterMatchMode="in"
            />

            <Column header="Actions" style={{ width: 90, textAlign: "center" }}
              body={(rowData) => {
                const isAdmin = user?.roles?.includes("admin");
                const isManager = (rowData.members || []).some(m => m.role === "manager" && m.userEmail === user?.email);
                if (!isAdmin && !isManager) return null;
                const isEditing = editingRowId === rowData.id;
                if (isEditing) {
                  return (
                    <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                      <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-sm"
                        tooltip="Save" onClick={() => handleEditSave(rowData)} />
                      <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-text p-button-sm"
                        tooltip="Cancel" onClick={handleEditCancel} />
                    </div>
                  );
                }
                return (
                  <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm p-button-info"
                    tooltip="Edit" onClick={() => handleEditStart(rowData)} />
                );
              }}
            />
          </DataTable>
        </div>
        <div className="card"></div>

        {/* ── Create Project Dialog ── */}
        <Dialog header="Create New Project" visible={showCreateDialog}
          style={{ width: "520px" }} modal draggable={false}
          onHide={() => setShowCreateDialog(false)}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm"
                onClick={() => setShowCreateDialog(false)} />
              <Button label="Create" icon="pi pi-check" className="p-button-sm subtable-primary-btn"
                loading={creatingUnlinkedProject} onClick={handleCreateProject} />
            </div>
          }
        >
          <div className="flex flex-column gap-3" style={{ padding: "8px 0" }}>
            <div className="field">
              <label htmlFor="cp-projectName">Project Name *</label>
              <InputText id="cp-projectName" value={createForm.projectName}
                onChange={(e) => setCreateForm(p => ({ ...p, projectName: e.target.value }))}
                className={createErrors.projectName ? "p-invalid" : ""} style={{ width: "100%" }} />
              {createErrors.projectName && <small className="p-error">{createErrors.projectName}</small>}
            </div>
            <div className="field">
              <label htmlFor="cp-targetName">Target Name</label>
              <InputText id="cp-targetName" value={createForm.targetName}
                onChange={(e) => setCreateForm(p => ({ ...p, targetName: e.target.value }))}
                placeholder="e.g. InhA, KatG" style={{ width: "100%" }} />
            </div>
            <div className="field">
              <label htmlFor="cp-modality">Modality *</label>
              <Dropdown id="cp-modality" value={createForm.modality}
                options={[
                  { label: "Small Molecule", value: "Small Molecule" },
                  { label: "RIPTAC", value: "RIPTAC" },
                  { label: "Antisense", value: "Antisense" },
                  { label: "Antibody", value: "Antibody" },
                ]}
                onChange={(e) => setCreateForm(p => ({ ...p, modality: e.value }))}
                placeholder="Select a Modality"
                className={createErrors.modality ? "p-invalid" : ""} style={{ width: "100%" }} />
              {createErrors.modality && <small className="p-error">{createErrors.modality}</small>}
            </div>
            <div className="field">
              <label htmlFor="cp-primaryOrg">Primary Organisation *</label>
              <Dropdown id="cp-primaryOrg" value={createForm.primaryOrg}
                options={(rootStore.generalStore.appVars?.orgs || []).map(o => ({ label: o.name, value: o.name }))}
                onChange={(e) => setCreateForm(p => ({ ...p, primaryOrg: e.value }))}
                placeholder="Select an Organisation"
                className={createErrors.primaryOrg ? "p-invalid" : ""} style={{ width: "100%" }} />
              {createErrors.primaryOrg && <small className="p-error">{createErrors.primaryOrg}</small>}
            </div>
            <div className="field">
              <label htmlFor="cp-manager">Project Manager</label>
              <Dropdown id="cp-manager" value={createForm.managerEmail}
                options={userOptions} onChange={(e) => setCreateForm(p => ({ ...p, managerEmail: e.value }))}
                placeholder="Select a Manager" style={{ width: "100%" }} filter />
            </div>
            <div className="field">
              <label htmlFor="cp-participants">Participants</label>
              <MultiSelect id="cp-participants" value={createForm.participantEmails}
                options={userOptions} onChange={(e) => setCreateForm(p => ({ ...p, participantEmails: e.value }))}
                placeholder="Select Participants" style={{ width: "100%" }} filter />
            </div>
          </div>
        </Dialog>
      </div>
    );
  }
  return <Loading />;
};

export default observer(Projects);
